
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Trash2 } from "lucide-react";

export const UserProviderAssignments = () => {
  const [newAssignment, setNewAssignment] = useState({
    userEmail: '',
    providerId: '',
    role: 'manager'
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all assignments with provider and profile information
  const { data: assignments, isLoading: assignmentsLoading } = useQuery({
    queryKey: ['user-provider-assignments'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_provider_assignments')
        .select('*');
      
      if (error) throw error;

      // Get profile and provider info for each assignment
      const assignmentsWithDetails = await Promise.all(
        data.map(async (assignment) => {
          const [profileResult, providerResult] = await Promise.all([
            supabase
              .from('profiles')
              .select('email, first_name, last_name')
              .eq('id', assignment.user_id)
              .single(),
            supabase
              .from('providers')
              .select('name, slug')
              .eq('id', assignment.provider_id)
              .single()
          ]);
          
          return {
            ...assignment,
            profiles: profileResult.data,
            providers: providerResult.data
          };
        })
      );
      
      return assignmentsWithDetails;
    },
  });

  // Fetch all providers
  const { data: providers } = useQuery({
    queryKey: ['providers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('providers')
        .select('id, name, slug')
        .order('name');
      
      if (error) throw error;
      return data;
    },
  });

  // Create assignment mutation
  const createAssignmentMutation = useMutation({
    mutationFn: async (assignment: typeof newAssignment) => {
      console.log('=== ASSIGNMENT ATTEMPT START ===');
      console.log('Attempting to assign user:', assignment.userEmail);
      
      // First, check if profile exists
      console.log('Searching for existing profile...');
      const { data: existingProfile, error: profileError } = await supabase
        .from('profiles')
        .select('id, email, first_name, last_name')
        .eq('email', assignment.userEmail)
        .maybeSingle();

      console.log('Existing profile search result:', { existingProfile, profileError });

      if (profileError) {
        console.error('Error checking for existing user:', profileError);
        throw new Error(`Database error when checking for user: ${profileError.message}`);
      }

      let userId: string;

      if (!existingProfile) {
        console.log('No profile found, attempting to create missing profile...');
        
        try {
          // Use the create_missing_profile function to handle users who exist in auth but not in profiles
          const { data: newUserId, error: createError } = await supabase
            .rpc('create_missing_profile', { user_email: assignment.userEmail });

          if (createError) {
            console.error('Error creating missing profile:', createError);
            throw new Error(`Failed to create profile: ${createError.message}`);
          }

          if (!newUserId) {
            throw new Error(`No user found with email "${assignment.userEmail}". The user must sign up first.`);
          }

          userId = newUserId;
          console.log('Successfully created missing profile for user ID:', userId);
        } catch (error: any) {
          console.error('Error in create_missing_profile:', error);
          throw new Error(`No user found with email "${assignment.userEmail}". The user must sign up first.`);
        }
      } else {
        userId = existingProfile.id;
        console.log('Found existing profile for user ID:', userId);
      }

      // Check if assignment already exists
      console.log('Checking for existing assignment...');
      const { data: existingAssignment, error: assignmentCheckError } = await supabase
        .from('user_provider_assignments')
        .select('id, role')
        .eq('user_id', userId)
        .eq('provider_id', assignment.providerId)
        .maybeSingle();

      if (assignmentCheckError) {
        console.error('Error checking existing assignment:', assignmentCheckError);
        throw new Error(`Error checking existing assignment: ${assignmentCheckError.message}`);
      }

      if (existingAssignment) {
        console.log('Assignment already exists:', existingAssignment);
        throw new Error(`User is already assigned to this provider with role: ${existingAssignment.role}`);
      }

      console.log('Creating new assignment for user:', userId, 'provider:', assignment.providerId);

      const { error: insertError } = await supabase
        .from('user_provider_assignments')
        .insert({
          user_id: userId,
          provider_id: assignment.providerId,
          role: assignment.role
        });

      if (insertError) {
        console.error('Error creating assignment:', insertError);
        throw new Error(`Failed to create assignment: ${insertError.message}`);
      }

      console.log('Assignment created successfully');
      console.log('=== ASSIGNMENT ATTEMPT END ===');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-provider-assignments'] });
      setNewAssignment({ userEmail: '', providerId: '', role: 'manager' });
      toast({
        title: "Success",
        description: "User assigned to provider successfully.",
      });
    },
    onError: (error) => {
      console.error('Assignment mutation error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to assign user to provider.",
        variant: "destructive",
      });
    },
  });

  // Delete assignment mutation
  const deleteAssignmentMutation = useMutation({
    mutationFn: async (assignmentId: string) => {
      const { error } = await supabase
        .from('user_provider_assignments')
        .delete()
        .eq('id', assignmentId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-provider-assignments'] });
      toast({
        title: "Success",
        description: "Assignment removed successfully.",
      });
    },
    onError: (error) => {
      console.error('Error deleting assignment:', error);
      toast({
        title: "Error",
        description: "Failed to remove assignment.",
        variant: "destructive",
      });
    },
  });

  const handleCreateAssignment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAssignment.userEmail || !newAssignment.providerId) {
      toast({
        title: "Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }
    createAssignmentMutation.mutate(newAssignment);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Create New Assignment</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateAssignment} className="space-y-4">
            <div>
              <Label htmlFor="userEmail">User Email</Label>
              <Input
                id="userEmail"
                type="email"
                placeholder="user@example.com"
                value={newAssignment.userEmail}
                onChange={(e) => setNewAssignment(prev => ({ ...prev, userEmail: e.target.value }))}
                required
              />
              <p className="text-sm text-muted-foreground mt-1">
                The user must have signed up first. If they signed up but don't have a profile, it will be created automatically.
              </p>
            </div>
            
            <div>
              <Label htmlFor="provider">Provider</Label>
              <Select
                value={newAssignment.providerId}
                onValueChange={(value) => setNewAssignment(prev => ({ ...prev, providerId: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a provider" />
                </SelectTrigger>
                <SelectContent>
                  {providers?.map((provider) => (
                    <SelectItem key={provider.id} value={provider.id}>
                      {provider.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="role">Role</Label>
              <Select
                value={newAssignment.role}
                onValueChange={(value) => setNewAssignment(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button type="submit" disabled={createAssignmentMutation.isPending}>
              {createAssignmentMutation.isPending ? "Creating..." : "Create Assignment"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Existing Assignments</CardTitle>
        </CardHeader>
        <CardContent>
          {assignmentsLoading ? (
            <p>Loading assignments...</p>
          ) : (
            <div className="space-y-2">
              {assignments?.map((assignment) => (
                <div key={assignment.id} className="flex items-center justify-between p-3 border rounded">
                  <div className="flex-1">
                    <div className="flex flex-col gap-1">
                      <p className="font-medium text-lg">
                        {assignment.profiles?.email || `User ID: ${assignment.user_id}`}
                      </p>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-sm text-muted-foreground">
                        {assignment.profiles?.first_name && assignment.profiles?.last_name && (
                          <span>{assignment.profiles.first_name} {assignment.profiles.last_name}</span>
                        )}
                        <span>Provider: {assignment.providers?.name || 'Unknown Provider'}</span>
                        <span>Role: {assignment.role}</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => deleteAssignmentMutation.mutate(assignment.id)}
                    disabled={deleteAssignmentMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {assignments?.length === 0 && (
                <p className="text-muted-foreground">No assignments found.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
